import { defineStore } from "pinia";
import type { DataValue } from "@/types.ts";
import { StoreModule } from "@/constants/store.ts";
import { axiosInstance } from "@/plugins/https.ts";
import {
  AdditionalWidgetType,
  OverviewCardWidgetType,
  SecurityCoverageWidgetType,
  WidgetType,
} from "@/constants/dashboard.ts";
import api from "@/_helpers/api.ts";
import { type Service, ServiceStatus } from "@/constants/cloud-apps.ts";
import { type BackupAndRestoreWidgetData } from "@/constants/cloud-backup.ts";
import moment from "moment";
import type { CloudAppService } from "@/_store/cloud-apps/cloud-apps.module.ts";
import type { TicketGeneratingSubscriptionModule } from "@/constants/workplaces.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";

export interface WidgetVulnerability {
  issue: string;
  count: number;
}

export interface OpenTicketsSummaryWidgetData {
  total: number;
  delta: number;
  modules: Array<{
    name: TicketGeneratingSubscriptionModule;
    count: number;
  }>;
}

export interface WorkspaceHealthScoreWidgetData {
  score: number;
  ticketsManagementPercent: number;
  securityGapsPercent: number;
}

export interface SecurityGapsWidgetData {
  total: number;
  delta: number;
  active: number;
  ignored: number;
}

export interface OverviewCardWidgetData {
  total: number;
  delta: number;
  totalLastMonth: number;
}

export interface WidgetTicketChartItem {
  date: number;
  tickets: number;
}

export interface WidgetData {
  activeVulnerabilities: WidgetVulnerability[];
}

export interface TicketWidgetData extends WidgetData {
  generatedTicketsHistory: WidgetTicketChartItem[];
  generatedTicketsDelta: number;
}

export interface UserDataGovernanceWidgetData extends TicketWidgetData {
  totalObjectsProcessed: number;
  hasConnectedServices: boolean;
  protectedUsersCount: number;
}

export interface EndpointSecurityWidgetData extends TicketWidgetData {
  protectedDevicesCount: number;
}

export interface EndpointDataGovernanceWidgetData extends TicketWidgetData {
  protectedDevicesCount: number;
}

export interface EmailSecurityWidgetData extends TicketWidgetData {
  emailsScanned: number;
  hasConnectedServices: boolean;
  isProxyActive?: boolean;
}

export interface CloudSecurityWidgetData extends TicketWidgetData {
  cloudServiceStatuses: Array<{
    name: Service;
    status: ServiceStatus;
    noLogs?: boolean;
  }>;
  hasConnectedServices: boolean;
}

export interface EdrWidgetData extends TicketWidgetData {
  processesCount: number;
  protectedDevicesCount: number;
  topProcesses?: Array<{
    name: string;
    percentage: number;
    count: number;
  }>;
}

export const startOfDayUtcValueFromDaysAgo = (daysAgo: number) =>
  moment().subtract(daysAgo, "days").startOf("day").utc().valueOf();

export const ninetyDaysAgoStartOfDay = () => startOfDayUtcValueFromDaysAgo(90);

export const thirtyDaysAgoStartOfDay = () => startOfDayUtcValueFromDaysAgo(30);

export interface SecurityAwarenessWidgetUserRecord {
  email: string;
  count: number;
}

export interface SecurityAwarenessWidgetData extends WidgetData {
  activated: boolean;
  daysToAllowList: number;
  simulationsFailed: number;
  simulationsReported: number;
  simulationsIgnored: number;
  coursesCompleted: number;
  coursesOverdue: number;
  coursesPending: number;
  adaptiveTrainingCourses: number;
  topUsersFailedPhishing: SecurityAwarenessWidgetUserRecord[];
  topUsersFailedTraining: SecurityAwarenessWidgetUserRecord[];
  services: CloudAppService[];
}

/**
 * Each property corresponds to a dashboard widget.
 */
export interface DashboardState {
  [WidgetType.CLOUD_SECURITY]: DataValue<CloudSecurityWidgetData>;
  [WidgetType.EMAIL_SECURITY]: DataValue<EmailSecurityWidgetData>;
  [WidgetType.ENDPOINT_SECURITY]: DataValue<EndpointSecurityWidgetData>;
  [WidgetType.USER_DATA_GOVERNANCE]: DataValue<UserDataGovernanceWidgetData>;
  [WidgetType.ENDPOINT_DATA_GOVERNANCE]: DataValue<EndpointDataGovernanceWidgetData>;
  [WidgetType.EDR]: DataValue<EdrWidgetData>;
  [WidgetType.SECURITY_AWARENESS_TRAINING]: DataValue<SecurityAwarenessWidgetData>;

  [AdditionalWidgetType.OPEN_TICKETS_SUMMARY]: DataValue<OpenTicketsSummaryWidgetData>;

  [OverviewCardWidgetType.PROTECTED_USERS]: DataValue<OverviewCardWidgetData>;
  [OverviewCardWidgetType.PROTECTED_DEVICES]: DataValue<OverviewCardWidgetData>;
  [OverviewCardWidgetType.OPEN_TICKETS]: DataValue<OverviewCardWidgetData>;
  [OverviewCardWidgetType.AUTO_RESOLVED_TICKETS]: DataValue<OverviewCardWidgetData>;

  [SecurityCoverageWidgetType.WORKSPACE_HEALTH]: DataValue<WorkspaceHealthScoreWidgetData>;
  [SecurityCoverageWidgetType.SECURITY_GAPS_WIDGET]: DataValue<SecurityGapsWidgetData>;

  backupAndRestore: DataValue<BackupAndRestoreWidgetData>;
}

export const useDashboardStore = defineStore(StoreModule.SINGLE_VIEW_DASHBOARD, {
  state: (): DashboardState => ({
    [WidgetType.CLOUD_SECURITY]: { loading: false },
    [WidgetType.EMAIL_SECURITY]: { loading: false },
    [WidgetType.ENDPOINT_SECURITY]: { loading: false },
    [WidgetType.USER_DATA_GOVERNANCE]: { loading: false },
    [WidgetType.ENDPOINT_DATA_GOVERNANCE]: { loading: false },
    [WidgetType.EDR]: { loading: false },
    [WidgetType.SECURITY_AWARENESS_TRAINING]: { loading: false },

    [AdditionalWidgetType.OPEN_TICKETS_SUMMARY]: { loading: false },

    [OverviewCardWidgetType.PROTECTED_USERS]: { loading: false },
    [OverviewCardWidgetType.PROTECTED_DEVICES]: { loading: false },
    [OverviewCardWidgetType.OPEN_TICKETS]: { loading: false },
    [OverviewCardWidgetType.AUTO_RESOLVED_TICKETS]: { loading: false },

    [SecurityCoverageWidgetType.WORKSPACE_HEALTH]: { loading: false },
    [SecurityCoverageWidgetType.SECURITY_GAPS_WIDGET]: { loading: false },

    backupAndRestore: { loading: false },
  }),

  actions: {
    /**
     * Fetches data for the Cloud Security widget.
     */
    async fetchCloudSecurity() {
      try {
        this.cloudSecurity = { loading: true };
        const request = {
          ...api.cloudSecurityWidget(),
          params: {
            resolvedFromTime: ninetyDaysAgoStartOfDay(),
          },
        };
        const { data } = await axiosInstance.request(request);
        this.cloudSecurity.data = data;
      } catch (error) {
        this.cloudSecurity.error = true;
        console.error("Error fetching Cloud Security data:", error);
      } finally {
        this.cloudSecurity.loading = false;
      }
    },

    /**
     * Fetches data for the Email Security widget from two endpoints.
     */
    async fetchEmailSecurity() {
      try {
        this.emailSecurity = { loading: true };
        const emailSecurityRequest = {
          ...api.emailSecurityWidget(),
          params: {
            resolvedFromTime: ninetyDaysAgoStartOfDay(),
          },
        };
        const proxyStatusRequest = {
          ...api.getEmailProxyStatus(),
          params: {
            direction: "INBOUND",
          },
        };

        const [emailsResponse, proxyStatusResponse] = await Promise.all([
          axiosInstance.request(emailSecurityRequest),
          axiosInstance.request(proxyStatusRequest).catch((error) => {
            console.error("Error fetching Email Proxy Status:", error);
            return { data: { isProxyActive: false } };
          }),
        ]);

        // Combine data from both endpoints
        this.emailSecurity.data = {
          ...emailsResponse.data,
          isProxyActive: proxyStatusResponse.data.isProxyActive,
        };
      } catch (error) {
        this.emailSecurity.error = true;
        console.error("Error fetching Email Security data:", error);
      } finally {
        this.emailSecurity.loading = false;
      }
    },

    /**
     * Fetches data for the Endpoint Security widget.
     */
    async fetchEndpointSecurity() {
      try {
        this.endpointSecurity = { loading: true };
        const request = {
          ...api.endpointSecurityWidget(),
          params: {
            resolvedFromTime: ninetyDaysAgoStartOfDay(),
          },
        };
        const { data } = await axiosInstance.request(request);
        this.endpointSecurity.data = data;
      } catch (error) {
        this.endpointSecurity.error = true;
        console.error("Error fetching Endpoint Security data:", error);
      } finally {
        this.endpointSecurity.loading = false;
      }
    },

    /**
     * Fetches data for the User Data Governance widget.
     */
    async fetchUserDataGovernance() {
      try {
        this.userDataGovernance = { loading: true };
        const request = {
          ...api.userDataGovernanceWidget(),
          params: {
            resolvedFromTime: ninetyDaysAgoStartOfDay(),
          },
        };
        const { data } = await axiosInstance.request(request);
        this.userDataGovernance.data = data;
      } catch (error) {
        this.userDataGovernance.error = true;
        console.error("Error fetching User Data Governance data:", error);
      } finally {
        this.userDataGovernance.loading = false;
      }
    },

    /**
     * Fetches data for the Endpoint Data Governance widget.
     */
    async fetchEndpointDataGovernance() {
      try {
        this.endpointDataGovernance = { loading: true };
        const request = {
          ...api.endpointDataGovernanceWidget(),
          params: {
            resolvedFromTime: ninetyDaysAgoStartOfDay(),
          },
        };
        const { data } = await axiosInstance.request(request);
        this.endpointDataGovernance.data = data;
      } catch (error) {
        this.endpointDataGovernance.error = true;
        console.error("Error fetching Endpoint Data Governance data:", error);
      } finally {
        this.endpointDataGovernance.loading = false;
      }
    },

    /**
     * Fetches data for the EDR widget.
     */
    async fetchEdr() {
      try {
        this.edr = { loading: true };
        const request = {
          ...api.edrWidget(),
          params: {
            resolvedFromTime: thirtyDaysAgoStartOfDay(),
          },
        };
        const { data } = await axiosInstance.request(request);
        this.edr.data = data;
      } catch (error) {
        this.edr.error = true;
        console.error("Error fetching EDR data:", error);
      } finally {
        this.edr.loading = false;
      }
    },

    /**
     * Fetches data for the Security Awareness widget from two endpoints.
     */
    async fetchSecurityAwareness() {
      try {
        this.securityAwarenessTraining = { loading: true };
        const securityAwarenessRequest = {
          ...api.securityAwarenessWidget(),
          params: {
            resolvedFromTime: ninetyDaysAgoStartOfDay(),
          },
        };

        const { data } = await axiosInstance.request(securityAwarenessRequest);

        this.securityAwarenessTraining.data = {
          ...data,
        };
      } catch (error) {
        this.securityAwarenessTraining.error = true;
        console.error("Error fetching Security Awareness data:", error);
      } finally {
        this.securityAwarenessTraining.loading = false;
      }
    },

    /**
     * Fetches data for the Cloud Backup (Backup & Restore) widget.
     */
    async fetchBackupAndRestore() {
      try {
        this.backupAndRestore = { loading: true };
        const { data } = await axiosInstance.request<BackupAndRestoreWidgetData>({
          ...api.backupAndRestoreDashboard(),
        });
        this.backupAndRestore.data = data;
      } catch (error) {
        this.backupAndRestore.error = true;
        console.error("Error fetching Backup and Restore data:", error);
      } finally {
        this.backupAndRestore.loading = false;
      }
    },

    /**
     * Fetches data for the Open tickets summary widget.
     */
    async fetchOpenTicketsSummary() {
      try {
        this.openTicketsSummary = { loading: true };
        const request = {
          ...api.openTicketsSummaryWidget(),
        };
        const { data } = await axiosInstance.request(request);
        this.openTicketsSummary.data = data;
      } catch (error) {
        this.openTicketsSummary.error = true;
        console.error("Error fetching Open tickets summary data:", error);
      } finally {
        this.openTicketsSummary.loading = false;
      }
    },

    /**
     * Fetches data for specified dashboard widgets concurrently.
     * If no widgets are specified, it fetches data for all of them.
     * @param widgetTypes An array of widget types to fetch data for.
     */
    async fetchAllWidgetData(widgetTypes: WidgetType[]) {
      const fetchActions: Record<WidgetType, () => Promise<void>> = {
        [WidgetType.CLOUD_SECURITY]: this.fetchCloudSecurity,
        [WidgetType.EMAIL_SECURITY]: this.fetchEmailSecurity,
        [WidgetType.ENDPOINT_SECURITY]: this.fetchEndpointSecurity,
        [WidgetType.USER_DATA_GOVERNANCE]: this.fetchUserDataGovernance,
        [WidgetType.ENDPOINT_DATA_GOVERNANCE]: this.fetchEndpointDataGovernance,
        [WidgetType.EDR]: this.fetchEdr,
        [WidgetType.SECURITY_AWARENESS_TRAINING]: this.fetchSecurityAwareness,
        [WidgetType.BACKUP_AND_RESTORE]: this.fetchBackupAndRestore,
      };

      const widgetRequests = widgetTypes
        .filter((type) => fetchActions[type]) // Ensure the action exists
        .map((type) => fetchActions[type].call(this)); // Fetch data for the specified widget

      // if some widgets are specified (means they're not disabled/restricted), fetch the open tickets summary widget
      const promises = widgetRequests.length
        ? widgetRequests.concat(this.fetchOpenTicketsSummary()) // Call the function
        : [];
      try {
        await Promise.all(promises);
      } catch (error) {
        console.error("An error occurred while fetching widget data:", error);
      }
    },
    /**
     * Fetches data for the Protected Users overview widget.
     */
    async fetchProtectedUsers() {
      try {
        this.protectedUsersOverview = { loading: true };
        const request = { ...api.protectedUsersOverview() };

        const { data } = await axiosInstance.request(request);
        this.protectedUsersOverview.data = data;
      } catch (error) {
        this.protectedUsersOverview.error = true;
        console.error("Error fetching Protected Users data:", error);
      } finally {
        this.protectedUsersOverview.loading = false;
      }
    },

    /**
     * Fetches data for the Protected Devices overview widget.
     */
    async fetchProtectedDevices() {
      try {
        this.protectedDevicesOverview = { loading: true };
        const request = { ...api.protectedDevicesOverview() };

        const { data } = await axiosInstance.request(request);
        this.protectedDevicesOverview.data = data;
      } catch (error) {
        this.protectedDevicesOverview.error = true;
        console.error("Error fetching Protected Devices data:", error);
      } finally {
        this.protectedDevicesOverview.loading = false;
      }
    },

    /**
     * Fetches data for the Open Tickets overview widget.
     */
    async fetchOpenTickets() {
      try {
        this.openTicketsOverview = { loading: true };
        const request = { ...api.openTicketsOverview() };
        const { data } = await axiosInstance.request(request);
        this.openTicketsOverview.data = data;
      } catch (error) {
        this.openTicketsOverview.error = true;
        console.error("Error fetching Open Tickets data:", error);
      } finally {
        this.openTicketsOverview.loading = false;
      }
    },

    /**
     * Fetches data for the Auto-Resolved Tickets overview widget.
     */
    async fetchAutoResolvedTickets() {
      try {
        this.autoResolvedTicketsOverview = { loading: true };
        const request = { ...api.autoResolvedTicketsOverview(ninetyDaysAgoStartOfDay()) };

        const { data } = await axiosInstance.request(request);
        this.autoResolvedTicketsOverview.data = data;
      } catch (error) {
        this.autoResolvedTicketsOverview.error = true;
        console.error("Error fetching Auto-Resolved Tickets data:", error);
      } finally {
        this.autoResolvedTicketsOverview.loading = false;
      }
    },

    /**
     * Fetches all overview card widgets concurrently.
     */
    async fetchAllOverviewCardWidgetData() {
      try {
        await Promise.all([
          this.fetchProtectedDevices(),
          this.fetchProtectedUsers(),
          this.fetchOpenTickets(),
          this.fetchAutoResolvedTickets(),
        ]);
      } catch (error) {
        console.error("An error occurred while fetching overview widgets data:", error);
      }
    },

    /**
     * Fetches data for the Workspace Health widget.
     */
    async fetchWorkspaceHealth() {
      try {
        this.workspaceHealthWidget = { loading: true };
        const request = { ...api.healthScoreWidget() };

        const { data } = await axiosInstance.request(request);
        this.workspaceHealthWidget.data = data;
        const { score, ticketsManagementPercent, securityGapsPercent } = data;
        const healthParam = `${GTMParams.HEALTH_SCORE} ${score}`;
        const ticketsMgmtParam = `${GTMParams.TICKETS_MGMT} ${ticketsManagementPercent}`;
        const securityGapsParam = `${GTMParams.SECURITY_GAPS} ${securityGapsPercent}`;
        trackGtmEvent(
          GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
          `${GTMParams.SINGLE_VIEW} - ${healthParam} - ${ticketsMgmtParam} - ${securityGapsParam}`
        );
      } catch (error) {
        this.workspaceHealthWidget.error = true;
        console.error("Error fetching Workspace Health data:", error);
      } finally {
        this.workspaceHealthWidget.loading = false;
      }
    },

    /**
     * Fetches data for the Security Gaps widget.
     */
    async fetchSecurityGaps() {
      try {
        this.securityGapsWidget = { loading: true };
        const request = { ...api.securityGapsWidget() };

        const { data } = await axiosInstance.request(request);
        this.securityGapsWidget.data = data;
      } catch (error) {
        this.securityGapsWidget.error = true;
        console.error("Error fetching Security Gaps data:", error);
      } finally {
        this.securityGapsWidget.loading = false;
      }
    },

    /**
     * Fetches specified security coverage widgets concurrently.
     *
     * @param {SecurityCoverageWidgetType[]} widgetTypes - An array of widget types to fetch.
     */
    async fetchSecurityCoverageWidgetsData(widgetTypes: SecurityCoverageWidgetType[]) {
      const fetchActions: Partial<Record<SecurityCoverageWidgetType, () => Promise<unknown>>> = {
        [SecurityCoverageWidgetType.WORKSPACE_HEALTH]: this.fetchWorkspaceHealth,
        [SecurityCoverageWidgetType.SECURITY_GAPS_WIDGET]: this.fetchSecurityGaps,
      };

      const widgetRequests = widgetTypes
        .map((type) => ({ type, action: fetchActions[type] }))
        .filter(
          (
            pair
          ): pair is {
            type: SecurityCoverageWidgetType;
            action: () => Promise<unknown>;
          } => {
            if (!pair.action) {
              console.warn(`No fetch action defined for widget type: ${pair.type}`);
              return false;
            }
            return true;
          }
        )
        .map((pair) => pair.action.call(this));

      if (widgetRequests.length === 0) {
        console.warn("No security coverage widget data to fetch.");
        return;
      }

      try {
        await Promise.all(widgetRequests);
      } catch (error) {
        console.error("An error occurred while fetching security coverage widget data:", error);
      }
    },
  },
});
